'use client'

import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import type { CityDatum } from '@/app/markets/page'

type CityCardProps = {
  city: CityDatum
  /** When provided, the card becomes a client-side link instead of relying on a wrapper onClick */
  href?: string
}

const formatPopulation = (value: number | null) => {
  if (!value) return 'N/A'
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`
  return value.toString()
}

export default function CityCard({ city, href }: CityCardProps) {
  const imageUrl = city.hero_image_url?.trim()
  const hasImage = Boolean(imageUrl && imageUrl !== 'null' && imageUrl !== 'undefined')
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)

  // Determine if we can use next/image (needs absolute URL with known host)
  const canUseNextImage = hasImage && (
    imageUrl!.startsWith('https://images.unsplash.com') ||
    imageUrl!.startsWith('https://tedgjvexhcsvbjhavevj.supabase.co')
  )

  const card = (
    <div className="group overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] shadow-sm transition hover:-translate-y-1 hover:shadow-lg dark:shadow-none">
      {/* Hero Section */}
      <div className="relative h-48 bg-gradient-to-br from-[var(--surface-2)] via-[var(--surface-1)] to-[var(--surface-3)]">
        {/* Skeleton pulse while image is loading */}
        {hasImage && !imageLoaded && !imageError && (
          <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-[var(--surface-2)] via-[var(--surface-3)] to-[var(--surface-2)]" />
        )}
        {hasImage && !imageError && canUseNextImage && (
          <Image
            src={imageUrl!}
            alt={`${city.primary_city} skyline`}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className={`object-cover transition-all duration-300 group-hover:scale-[1.03] ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            referrerPolicy="no-referrer"
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}
        {hasImage && !imageError && !canUseNextImage && (
          <img
            src={encodeURI(imageUrl as string)}
            alt={`${city.primary_city} skyline`}
            className={`absolute inset-0 h-full w-full object-cover transition-all duration-300 group-hover:scale-[1.03] ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            referrerPolicy="no-referrer"
            loading="lazy"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(0,0,0,0.12),_transparent_50%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />

        {/* Top badges (essential info only) */}
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {city.veterans_top_50_rank != null && (
            <span className="rounded-xl border border-white/20 bg-black/60 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
              Top 50 Vet City
            </span>
          )}
          {city.no_state_income_tax && (
            <span className="rounded-xl border border-emerald-200/40 bg-emerald-500/90 px-3 py-1 text-xs font-semibold text-white backdrop-blur-sm">
              Tax Free
            </span>
          )}
        </div>
      </div>

      {/* Content Section - Minimal */}
      <div className="space-y-3 p-4">
        {/* City Name */}
        <div>
          <h3 className="text-lg font-bold text-[var(--text-1)]">
            {city.primary_city}, {city.primary_state}
          </h3>
        </div>

        {/* Key Stats */}
        <div className="flex items-center gap-3 text-sm text-[var(--text-2)]">
          <span>Pop. {formatPopulation(city.rank_population)}</span>
          {city.median_home_price != null && (
            <>
              <span className="text-[var(--text-3)]">·</span>
              <span>${Math.round(city.median_home_price).toLocaleString()}</span>
            </>
          )}
        </div>

        {/* Feature Badges - Quick visual indicators */}
        <div className="flex flex-wrap gap-1.5">
          {city.tech_hub && (
            <span className="rounded-full border border-blue-900/20 bg-blue-900/5 px-2 py-0.5 text-[10px] font-medium text-blue-900/70 dark:border-blue-300/20 dark:bg-blue-300/10 dark:text-blue-300">
              Tech Hub
            </span>
          )}
          {city.military_hub && (
            <span className="rounded-full border border-blue-900/20 bg-blue-900/5 px-2 py-0.5 text-[10px] font-medium text-blue-900/70 dark:border-blue-300/20 dark:bg-blue-300/10 dark:text-blue-300">
              Military Hub
            </span>
          )}
          {city.cheap_housing && (
            <span className="rounded-full border border-blue-900/20 bg-blue-900/5 px-2 py-0.5 text-[10px] font-medium text-blue-900/70 dark:border-blue-300/20 dark:bg-blue-300/10 dark:text-blue-300">
              Affordable
            </span>
          )}
          {city.gun_friendly && (
            <span className="rounded-full border border-blue-900/20 bg-blue-900/5 px-2 py-0.5 text-[10px] font-medium text-blue-900/70 dark:border-blue-300/20 dark:bg-blue-300/10 dark:text-blue-300">
              Pro-Gun
            </span>
          )}
          {city.high_salaries && (
            <span className="rounded-full border border-blue-900/20 bg-blue-900/5 px-2 py-0.5 text-[10px] font-medium text-blue-900/70 dark:border-blue-300/20 dark:bg-blue-300/10 dark:text-blue-300">
              High Salaries
            </span>
          )}
          {city.near_va_medical && (
            <span className="rounded-full border border-blue-900/20 bg-blue-900/5 px-2 py-0.5 text-[10px] font-medium text-blue-900/70 dark:border-blue-300/20 dark:bg-blue-300/10 dark:text-blue-300">
              Near VA Hospital
            </span>
          )}
        </div>

        {/* Subtle CTA text - encouraging discovery */}
        <p className="text-xs text-[var(--text-3)] pt-2">Click to explore</p>
      </div>
    </div>
  )

  if (href) {
    return (
      <Link href={href} className="block cursor-pointer">
        {card}
      </Link>
    )
  }

  return card
}
